/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import React, { useEffect, useRef, useState } from "react"
import { useTranslations } from "common/utils/i18n"
import { isUndefined } from "lodash"
import { storageHelper } from "utils/storage"
import { ALCOHOL } from "../ItemDrawer/constant"
import TaggingField from "./TaggingField"

const ITEM_CONTAIN_ALCOHOL = 1

export interface AlcoholTaggingProps extends FormComponentProps {
  alcoholPrediction?: boolean
  showAttributeDrawer: boolean
  specialItemType?: string
  onAlcoholChange: (value: number) => void
}

const AlcoholTagging = ({
  form,
  specialItemType,
  alcoholPrediction,
  showAttributeDrawer,
  onAlcoholChange,
}: AlcoholTaggingProps) => {
  const [showTooltip, setShowTooltip] = useState(false)

  const [yesText, noText, placeholderText, tooltipText] = useTranslations([
    "common.yes",
    "common.no",
    "common.pleaseChoose",
    "alcoholSetting.infoTooltip",
  ])

  const options = [
    { label: yesText, value: 1 },
    { label: noText, value: 0 },
  ]

  const ref = useRef<HTMLDivElement>(null)

  const alcoholTaggingTooltipShowed = storageHelper.alcoholTaggingTooltipShowed()

  useEffect(() => {
    /**
     * show tooltip if:
     * - attribute drawer showed
     * - successfully retrieves alcohol prediction from API and the the value is 1 (Yes)
     * - alcoholTaggingTooltipShowed on local storage is empty
     */
    if (
      showAttributeDrawer &&
      alcoholPrediction &&
      !alcoholTaggingTooltipShowed
    ) {
      ref?.current?.scrollIntoView({ behavior: "smooth", block: "center" })
      setShowTooltip(true)
      storageHelper.setAlcoholTaggingTooltipShowed()
    }
  }, [showAttributeDrawer, alcoholPrediction, alcoholTaggingTooltipShowed])

  const getInitialValue = () => {
    if (!isUndefined(alcoholPrediction)) return Number(alcoholPrediction)

    if (specialItemType === ALCOHOL) return ITEM_CONTAIN_ALCOHOL

    return undefined
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setShowTooltip(false)
    }
  }

  const handleChange = (value: number) => {
    onAlcoholChange(value)
  }

  return (
    <TaggingField
      containerRef={ref}
      form={form}
      fieldName="alcoholTagging"
      label="Item Contain Alcohol"
      initialValue={getInitialValue()}
      options={options}
      placeholder={placeholderText}
      onChange={handleChange}
      showTooltip={showTooltip}
      tooltipText={tooltipText}
      onTooltipOpenChange={handleOpenChange}
    />
  )
}

export default AlcoholTagging
